// Level catalog. Each entry is fully self-describing so adding a new level
// is just appending an object — no other module needs to change.

import { DEFAULT_MAP } from './map.js';
import type { Objective } from './types.js';

export interface LevelDef {
  /** Stable id used in URLs and localStorage keys. */
  id: string;
  /** Short display name shown on the level card. */
  name: string;
  /** One-line flavour text on the level card. */
  description: string;
  /** ASCII map. Same conventions as DEFAULT_MAP. */
  map: readonly string[];
  /** S-rank time threshold in seconds. Also drives the D-rank timeout (× 2). */
  targetTime: number;
  /** What the player has to do before the extraction pad activates.
   * Defaults to `{ kind: 'extract' }` — the legacy "go from A to B". */
  objective?: Objective;
  /** When true, the renderer applies a vision mask: the player only sees
   * a disc around themselves + a forward cone, both raycast-clipped by
   * walls. Anything outside is hidden. */
  fogOfWar?: boolean;
}

/** Level 4 — collect 3 intel files scattered across two rooms, then extract. */
const VOL_DE_PLANS: readonly string[] = [
  '##############################',
  '#P.....#.................#...#',
  '#......#.....L...........#...#',
  '#......#.................#...#',
  '#......#######.....#######...#',
  '#............#.....#.........#',
  '#......*.....#..L..#.........#',
  '#............#.....#.........#',
  '#............#.....#...*.....#',
  '######...#####.....#####...###',
  '#............................#',
  '#.........g..................#',
  '#####.......######.....#######',
  '#...........#....#...........#',
  '#....L......#....#......L....#',
  '#....*......#....#............',
  '#...........#....#...........#',
  '#######...###....######...####',
  '#............................#',
  '#.........L..........h......E#',
  '##############################',
];

/** Level 7 — fog-of-war: the player only sees a disc + forward cone. */
const NUIT_NOIRE: readonly string[] = [
  '######################',
  '#P...................#',
  '#....................#',
  '#........L...........#',
  '####.######.##########',
  '#....................#',
  '#......g.............#',
  '#....................#',
  '####.######.##########',
  '#....*...........*...#',
  '#....................#',
  '#......h.............#',
  '#....................#',
  '####.######.##########',
  '#....L...............#',
  '#...................E#',
  '######################',
];

/** Level 6 — security cameras you can hack. Camera count tuned: the
 * earlier 6-camera layout had cones blanketing 75% of the screen,
 * which read as overwhelming rather than tactical. Three cameras,
 * each guarding a chokepoint, leaves shadow lanes between them. */
const SURVEILLANCE: readonly string[] = [
  '##############################',
  '#P...........................#',
  '#............................#',
  '#......L..........v.....L....#',
  '#............................#',
  '#............................#',
  '########.##########.##########',
  '#.....*..............*.......#',
  '#............................#',
  '#............................#',
  '#......^...................<.#',
  '#............................#',
  '########.##########.##########',
  '#............................#',
  '#............................#',
  '#......L....g..........L.....#',
  '#............................#',
  '#............................E',
  '##############################',
];

/** Level 5 — clean sweep: K.O. all guards, then extract. */
const NETTOYAGE: readonly string[] = [
  '########################',
  '#P.....................#',
  '#......L....L....L.....#',
  '#......................#',
  '#.......g..............#',
  '#......................#',
  '#####.######.######....#',
  '#......................#',
  '#............h.........#',
  '#......L....L....L.....#',
  '#......................#',
  '#.....................i#',
  '#......................#',
  '#......................E',
  '########################',
];

/** Level 1 — small, one guard, plenty of shadow. The training stage. */
const ENTREPOT: readonly string[] = [
  '##################',
  '#P...............#',
  '#................#',
  '#.....L..........#',
  '#................#',
  '#.....g.......g..#',
  '#................#',
  '#................#',
  '#......#####.....#',
  '#......#...#.....#',
  '#......#.L.#.....#',
  '#......#####....E#',
  '##################',
];

/** Level 3 — wide, lit, three guards. Force creative use of boxes/decoys.
 * Lamp positions intentionally irregular: the previous strict 5×4 grid
 * read as a synthetic test pattern. Now alternates row spacing + drops
 * a couple of lamps to leave shadow corridors for cardboard sneaking. */
const LABO: readonly string[] = [
  '##############################',
  '#P............L..............#',
  '#.....L................L.....#',
  '#..........L.....L...........#',
  '#............................#',
  '#......g.................h...#',
  '#...L................L.......#',
  '#...........L....L...........#',
  '#......L..................L..#',
  '#............................#',
  '#L....................L......#',
  '#.........L........L.........#',
  '#...L........................#',
  '#............i.....L.........#',
  '#......L....................L#',
  '#..........L.....L...........#',
  '#............................#',
  '#............................E',
  '##############################',
];

export const LEVELS: ReadonlyArray<LevelDef> = [
  {
    id: 'entrepot',
    name: 'Entrepôt',
    description: "Mission d'entraînement — un seul garde, beaucoup d'ombre.",
    map: ENTREPOT,
    targetTime: 50,
  },
  {
    id: 'bureaux',
    name: 'Bureaux',
    description: 'Patrouilles imbriquées — trois gardes, alternance ombre / lumière.',
    map: DEFAULT_MAP,
    targetTime: 90,
  },
  {
    id: 'labo',
    name: 'Labo de recherche',
    description: 'Lumineux, peu d ombre — abuse des boîtes et leurres.',
    map: LABO,
    targetTime: 120,
  },
  {
    id: 'vol-de-plans',
    name: 'Vol de plans',
    description:
      "Récupère 3 dossiers ★ avant de t'extraire. La porte reste verrouillée tant qu'il en manque.",
    map: VOL_DE_PLANS,
    targetTime: 100,
    objective: { kind: 'collect-and-extract' },
  },
  {
    id: 'nettoyage',
    name: 'Nettoyage',
    description:
      'K.O. les trois gardes (par derrière) avant de partir. Aucun ne doit rester debout.',
    map: NETTOYAGE,
    targetTime: 90,
    objective: { kind: 'eliminate-and-extract' },
  },
  {
    id: 'surveillance',
    name: 'Centre de surveillance',
    description:
      'Caméras qui scannent les couloirs. Approche, pirate (H) — ou attends que le balayage tourne. 2 dossiers à voler.',
    map: SURVEILLANCE,
    targetTime: 110,
    objective: { kind: 'collect-and-extract' },
  },
  {
    id: 'nuit-noire',
    name: 'Opération nocturne',
    description:
      "Tu ne vois que ce que ta lampe et ton instinct portent. Avance prudemment dans le noir — les murs te cachent les gardes autant qu'ils te cachent.",
    map: NUIT_NOIRE,
    targetTime: 100,
    objective: { kind: 'collect-and-extract' },
    fogOfWar: true,
  },
];

export function getLevel(id: string): LevelDef | undefined {
  return LEVELS.find((l) => l.id === id);
}

export function getDefaultLevel(): LevelDef {
  return LEVELS[0]!;
}
